import React, { memo, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Button, rem } from 'design-system';
import { Tier, TierPeriod, isPremiumTier, tierFromProductId, tierGreaterThan, tierLessThan, } from 'features/premium/entities';
import useColors from 'hooks/useColors';
import { getThemeGradient } from 'features/premium/colors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { usePromoDiscountBanner } from 'features/promotions/hooks';
import Modal from 'utils/packages/Modal';
import { useAppSelector } from 'store/hooks';
import { formatTryFor0 } from 'utils';
import { selectDefaultCurrency, selectIsLoggedIn } from 'reducers/selectors';
import { getAnnualDiscountPercentage, keepYearlyText, switchToYearlyText, } from '../annualDiscount';
import TierFlatList from './TierFlatList';
import { tierThemes } from '../benefits';
import TierFlatListTrial from './TierFlatListTrial';
import { selectEligibleFreeTrialDays, selectShouldSeePremiumTrialScreen, } from '../selectors';
import { selectUserTier } from '../selectors/tier';
const PremiumTierView = ({ tMap, tier, prices, isInTab, onScroll, periodMap, activeTier, renderTier, screenWidth, canUseStripe, showSelector = true, isFetchingPro, showBackButton, onPressBenefit, isOnReverseTrial, onContinueOnFree, autoRenewProductId, openSubscribePopup, isFetchingPeriodMap, showDowngradePeriodButton, showNewViewAllPlansScreen, }) => {
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const themeGradient = getThemeGradient(tier, colors);
    const canChangePeriod = isPremiumTier(tier) ? periodMap[tier] : undefined;
    const { paddingHorizontal, paddingHorizontalStyle } = useAppFrameDimensions(isInTab);
    const buttonStyle = useMemo(() => {
        if (isInTab && !paddingHorizontal) {
            return [{ marginHorizontal: paddingHorizontal }, styles.button];
        }
        return [{ marginHorizontal: paddingHorizontal }];
    }, [isInTab, paddingHorizontal]);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const discountBanner = usePromoDiscountBanner();
    const onPress = useCallback(() => {
        const cb = () => {
            if (isPremiumTier(tier)) {
                openSubscribePopup(tier);
            }
            else if (onContinueOnFree) {
                onContinueOnFree();
            }
        };
        if (!discountBanner ||
            tier !== discountBanner.matchedSubscription.tier ||
            !isLoggedIn) {
            cb();
            return;
        }
        Modal.showConfirmation({
            title: 'You have an active offer',
            subtitle: `You have a limited time offer for ${discountBanner.productName} ${discountBanner.matchedSubscription.period === TierPeriod.monthly
                ? 'monthly'
                : 'yearly'} plan. Do you want to explore it?`,
            confirmButtonTitle: 'Skip the offer',
            cancelButtonTitle: 'Discover the offer',
            onConfirm: cb,
            onPressCancel: discountBanner.onOpen,
        });
    }, [discountBanner, isLoggedIn, onContinueOnFree, openSubscribePopup, tier]);
    const shouldSeePremiumTrialScreen = useAppSelector((store) => selectShouldSeePremiumTrialScreen(store, tier));
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const userTier = useAppSelector(selectUserTier);
    const isUpgrade = tierGreaterThan(tier, userTier);
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const showTryFor0 = !!(shouldSeePremiumTrialScreen && isUpgrade && eligibleFreeTrialDays) ||
        !!(emmaProStatus.isOnFreeTrial &&
            emmaProStatus.currentFreeTrialDays &&
            isUpgrade);
    if (!renderTier) {
        return <View key={tier} style={{ width: screenWidth }}/>;
    }
    const showButton = (tier !== Tier.free || onContinueOnFree) &&
        ((activeTier !== tier &&
            (!autoRenewProductId ||
                tierFromProductId(autoRenewProductId) !== tier)) ||
            isOnReverseTrial ||
            (canChangePeriod &&
                (canChangePeriod === TierPeriod.yearly || showDowngradePeriodButton)) ||
            (showDowngradePeriodButton && isFetchingPeriodMap));
    const monthlyPrice = isPremiumTier(tier)
        ? prices?.[TierPeriod.monthly][tier]
        : undefined;
    const yearlyPrice = isPremiumTier(tier)
        ? prices?.[TierPeriod.yearly][tier]
        : undefined;
    const currency = yearlyPrice?.currency ||
        prices?.[TierPeriod.yearly][Tier.plus]?.currency ||
        userCurrency;
    const isDowngradingTier = tier === activeTier &&
        emmaProStatus.autoRenewTier &&
        tierLessThan(emmaProStatus.autoRenewTier, userTier);
    const isDowngradingPeriodSameTier = tier === activeTier &&
        emmaProStatus.autoRenewTier === userTier &&
        emmaProStatus.period === 'yearly' &&
        emmaProStatus.autoRenewPeriod === 'monthly';
    const canUpgradeToYearly = tier === activeTier && emmaProStatus.period === 'monthly';
    const annualDiscount = getAnnualDiscountPercentage(monthlyPrice?.price, yearlyPrice?.price);
    const buttonText = (() => {
        if (tier === activeTier && canChangePeriod === TierPeriod.monthly) {
            return `Change ${tierThemes[tier].name} to Monthly`;
        }
        if (showTryFor0) {
            return formatTryFor0(currency);
        }
        if (isDowngradingTier && !canUpgradeToYearly) {
            return `Keep ${tierThemes[tier].name}`;
        }
        if (isDowngradingPeriodSameTier) {
            return keepYearlyText(annualDiscount);
        }
        if (canUpgradeToYearly) {
            return switchToYearlyText(annualDiscount);
        }
        return 'Continue';
    })();
    const isFetching = Boolean(canUseStripe
        ? isFetchingPro
        : isFetchingPro || (showDowngradePeriodButton && isFetchingPeriodMap));
    return (<View key={tier} style={{ width: screenWidth }}>
      {showNewViewAllPlansScreen ? (<TierFlatListTrial scrollRef={tMap[tier]} isActive={tier === activeTier} tier={tier} localizedPrice={monthlyPrice?.localizedPrice} yearlyPrice={yearlyPrice?.price} currency={currency} onScroll={onScroll} hasButton={Boolean(showButton || showBackButton)} contentContainerStyle={paddingHorizontalStyle} showSelector={showSelector}/>) : (<TierFlatList scrollRef={tMap[tier]} isActive={tier === activeTier} tier={tier} localizedPrice={monthlyPrice?.localizedPrice} yearlyPrice={yearlyPrice?.price} currency={currency} onScroll={onScroll} onPress={onPress} onPressBenefit={onPressBenefit} hasButton={Boolean(showButton || showBackButton)} contentContainerStyle={paddingHorizontalStyle} showSelector={showSelector} isFetching={isFetching}/>)}

      {showBackButton ? (<Button floating custom textColor={colors.text.onPrimary} gradientColors={themeGradient?.colors} onPress={() => navigation?.pop()} title="Go back" containerStyle={buttonStyle}/>) : (Boolean(showButton) && (<Button floating custom textColor={colors.text.onPrimary} gradientColors={themeGradient?.colors} isFetching={isFetching} onPress={onPress} title={buttonText} containerStyle={buttonStyle}/>))}
    </View>);
};
export default memo(PremiumTierView);
const styles = StyleSheet.create({
    button: {
        bottom: rem(16),
    },
});
